import { createContext, useContext, useState, useEffect, useRef, ReactNode } from "react";

interface AccessGateContextType {
  hasAccess: boolean;
  grantAccess: () => void;
  revokeAccess: () => void;
}

const AccessGateContext = createContext<AccessGateContextType | undefined>(undefined);

// Routes that bypass the coming soon page
const BYPASS_ROUTES = [
  "/split/",
  "/accept-invite",
  "/privacy-policy",
  "/terms-of-service",
  "/contact",
  "/refund-policy",
  "/shakatah-checkout",
  "/shakatah-split",
  "/oauth-success",
  "/shopify-redirect",
  "/customer-login",
  "/reset-password",
  "/merchant-login",
  "/merchant/onboarding",
  "/merchant-onboarding",
  "/merchant-dashboard",
  "/merchant/widget-guide",
  "/merchant",
  "/admin",
  "/login",
];

// Export this function first so it can be used in initial state
export function shouldBypassGate(pathname: string): boolean {
  return BYPASS_ROUTES.some(route => pathname.startsWith(route));
}

// NOTE: Using function declaration instead of const arrow to prevent TDZ errors in production builds
export function AccessGateProvider({ children }: { children: ReactNode }) {
  const [hasAccess, setHasAccess] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    
    // If already granted, return true
    if (localStorage.getItem("shakatah_access") === "granted") {
      return true;
    }
    
    // Check if on a bypass route - grant access immediately to prevent flash
    // Don't write to localStorage here to avoid side effects in initializer
    if (shouldBypassGate(window.location.pathname)) {
      return true;
    }
    
    // Check for OAuth success param or access code in URL - check BEFORE first render
    const params = new URLSearchParams(window.location.search);
    if (params.get("oauth_success") === "true") {
      return true;
    }
    
    // Check for access code (e.g., from Shopify OAuth redirect)
    const accessParam = params.get("access");
    if (accessParam?.toLowerCase() === "shakatah2025") {
      return true;
    }
    
    return false;
  });

  // Persist access to localStorage AFTER initial render (avoid side effects in state initializer)
  const hasPersistedAccess = useRef(false);
  
  useEffect(() => {
    if (hasAccess && !hasPersistedAccess.current) {
      hasPersistedAccess.current = true;
      localStorage.setItem("shakatah_access", "granted");
    }
  }, [hasAccess]);

  // Clean up access code from URL after mount to prevent it showing in browser
  // Use a ref to ensure this only runs once
  const hasCleanedUrl = useRef(false);
  
  useEffect(() => {
    if (hasCleanedUrl.current) return;
    
    const params = new URLSearchParams(window.location.search);
    const accessParam = params.get("access");
    
    if (accessParam?.toLowerCase() === "shakatah2025") {
      hasCleanedUrl.current = true;
      params.delete("access");
      const newUrl = params.toString() 
        ? `${window.location.pathname}?${params.toString()}`
        : window.location.pathname;
      window.history.replaceState({}, "", newUrl);
    }
  }, []);

  // The access code check is now done in useState initializer to prevent flash
  // This useEffect is kept for any future dynamic access code handling if needed

  const grantAccess = () => {
    localStorage.setItem("shakatah_access", "granted");
    setHasAccess(true);
  };

  const revokeAccess = () => {
    localStorage.removeItem("shakatah_access");
    setHasAccess(false);
  };

  return (
    <AccessGateContext.Provider value={{ hasAccess, grantAccess, revokeAccess }}>
      {children}
    </AccessGateContext.Provider>
  );
}

export function useAccessGate() {
  const context = useContext(AccessGateContext);
  if (!context) {
    throw new Error("useAccessGate must be used within AccessGateProvider");
  }
  return context;
}
